import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useUser } from "../../contexts/UserContext";
import axiosInstance from "../../config/axios";
import { Club } from "../../types/ClubTypes";

export const UsersAdminPage: React.FC = () => {
  const { users, fetchUsers, inviteUser } = useUser();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [clubId, setClubId] = useState<number | "">("");
  const [clubs, setClubs] = useState<Club[]>([]);
  const [userType, setUserType] = useState("COACH");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
    axiosInstance
      .get("/clubs/")
      .then((response) => setClubs(response.data))
      .catch((err) => setError(err?.message ?? "Unable to fetch clubs"));
  }, []);

  const onCreate = async () => {
    try {
      await inviteUser({
        first_name: firstName,
        last_name: lastName,
        email,
        club: Number(clubId),
        user_type: userType,
      });
      setMessage("User invited with temporary password 'password'.");
      setOpen(false);
      setFirstName("");
      setLastName("");
      setEmail("");
      setClubId("");
      setUserType("COACH");
      await fetchUsers();
    } catch (err: any) {
      setError(err?.message ?? "Unable to add user.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Users</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Add User
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Username</TableCell>
            <TableCell>User Type</TableCell>
            <TableCell>Club</TableCell>
            <TableCell>Must Change Password</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{`${user.first_name || ""} ${user.last_name || ""}`.trim() || "-"}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.user_type}</TableCell>
              <TableCell>{user.club?.name ?? "-"}</TableCell>
              <TableCell>{user.must_change_password ? "Yes" : "No"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <TextField
            autoFocus
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="new-user-club-label">Club</InputLabel>
            <Select
              labelId="new-user-club-label"
              label="Club"
              value={clubId}
              onChange={(e) => setClubId(e.target.value as number)}
            >
              {clubs.map((club) => (
                <MenuItem key={club.id} value={club.id}>
                  {club.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="new-user-type-label">User Type</InputLabel>
            <Select
              labelId="new-user-type-label"
              label="User Type"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="COACH">Coach</MenuItem>
              <MenuItem value="PLAYER">Player</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="info">Temporary password will be set to: password</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreate} disabled={!firstName || !lastName || !email || !clubId}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(message)} autoHideDuration={4000} onClose={() => setMessage(null)}>
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(error)} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
